import { PropsWithChildren, useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import TuneIcon from '@material-ui/icons/Tune';
import FlightTakeoffIcon from '@material-ui/icons/FlightTakeoff';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  sidebarConfig,
  SidebarDivider,
  SidebarExpandButton,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import LogoutIcon from '@material-ui/icons/ExitToApp';
import { identityApiRef, useApi } from '@backstage/core-plugin-api';
import { useLocation } from 'react-router-dom';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const SignOutSidebarItem = () => {
  const identityApi = useApi(identityApiRef);
  return <SidebarItem icon={LogoutIcon} text="Sign Out" onClick={() => identityApi.signOut()} />;
};

// The wizard is reached from the runs list's New release button, so light Release up there too.
const useReleaseItemStyles = makeStyles(theme => ({
  active: {
    '& a': {
      borderLeft: `solid 3px ${theme.palette.navigation.indicator}`,
      color: theme.palette.navigation.selectedColor,
    },
  },
}));

const RELEASE_MANAGEMENT_GROUP = 'group:default/backstage-release-management';

// Hides the release tools from anyone outside a *-devops group or release management. Nav only: the
// routes stay reachable by URL, and the release-conductor backend enforces its own group check on
// every write.
export const ReleaseSidebarItems = () => {
  const identityApi = useApi(identityApiRef);
  const classes = useReleaseItemStyles();
  const { pathname } = useLocation();
  const active = pathname.startsWith('/release-conductor');
  const [isDevops, setIsDevops] = useState(false);
  useEffect(() => {
    identityApi
      .getBackstageIdentity()
      .then(({ ownershipEntityRefs }) =>
        setIsDevops(
          ownershipEntityRefs.some(
            ref => ref === RELEASE_MANAGEMENT_GROUP || (ref.startsWith('group:') && ref.endsWith('-devops')),
          ),
        ),
      )
      .catch(() => setIsDevops(false));
  }, [identityApi]);
  if (!isDevops) return null;
  return (
    <>
      <SidebarDivider />
      <div className={active ? classes.active : undefined}>
        <SidebarItem icon={FlightTakeoffIcon} to="release-ops" text="Release" />
      </div>
    </>
  );
};

export const Root = ({ children }: PropsWithChildren<{}>) => (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Browse the catalog */}
        <SidebarItem icon={HomeIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={ExtensionIcon} to="apis?filters[kind]=api" text="APIs" />
        <SidebarItem
          icon={AccountTreeIcon}
          to="step-functions?filters[kind]=component&filters[tags]=step-functions"
          text="Step Functions"
        />
        <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
        <SidebarDivider />
        {/* Tools */}
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        <SidebarItem icon={TuneIcon} to="config-editor" text="Config Editor" />
        <ReleaseSidebarItems />
        <SidebarDivider />
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarExpandButton />
      <SidebarDivider />
      {/* Notifications hidden for now — not in use */}
      {/* <NotificationsSidebarItem /> */}
      <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
        <SidebarSettings />
      </SidebarGroup>
      <SidebarDivider />
      <SignOutSidebarItem />
    </Sidebar>
    {children}
  </SidebarPage>
);
